import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Shield, Globe, Zap, Users } from 'lucide-react';

const values = [
  { icon: Shield, title: 'Trust First', desc: 'Every shipment is insured and handled with complete accountability.' },
  { icon: Zap,    title: 'Speed',       desc: 'Instant dispatch, real-time updates, zero delays on committed timelines.' },
  { icon: Globe,  title: 'Pan-India',   desc: 'A growing network across 250+ cities connecting every corner of India.' },
  { icon: Users,  title: 'People',      desc: 'A dedicated team of logistics professionals working 24/7 for your cargo.' },
];

const AboutUs = () => (
  <div className="bg-brand-bg min-h-screen pt-28 pb-20 px-6 md:px-12">
    <div className="max-w-5xl mx-auto">

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="text-center mb-20"
      >
        <p className="text-brand-orange font-black tracking-[0.4em] text-xs uppercase mb-4">Our Story</p>
        <h1 className="text-5xl md:text-7xl font-black text-brand-black tracking-tighter leading-none mb-6">
          ABOUT <span className="text-brand-orange">PKROUTEX</span>
        </h1>
        <p className="text-brand-muted text-lg max-w-2xl mx-auto leading-relaxed">
          Born from a vision to modernize Indian logistics, PKRouteX is a next-generation transport company built on technology, trust, and a relentless commitment to on-time delivery.
        </p>
      </motion.div>

      {/* Story block */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="bg-white rounded-3xl p-8 md:p-12 border border-black/6 shadow-sm mb-12"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-brand-orange font-black tracking-widest text-xs uppercase mb-4">Who We Are</p>
            <h2 className="text-3xl md:text-4xl font-black text-brand-black mb-5 leading-tight">
              Redefining Logistics <br />For Modern India
            </h2>
            <p className="text-brand-muted leading-relaxed mb-4">
              PKRouteX was founded with one mission — to make commercial logistics as seamless, transparent, and reliable as possible for businesses of every size.
            </p>
            <p className="text-brand-muted leading-relaxed">
              From small parcel deliveries to large-scale interstate industrial shipments, we operate a premium fleet of vehicles managed by experienced professionals who understand the urgency of your business.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Cities Covered', value: '250+' },
              { label: 'Deliveries Done', value: '50K+' },
              { label: 'Fleet Vehicles', value: '1200+' },
              { label: 'Satisfaction Rate', value: '99%' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-brand-bg rounded-2xl p-5 text-center border border-black/5"
              >
                <p className="text-3xl font-black text-brand-orange mb-1">{s.value}</p>
                <p className="text-[10px] text-brand-muted font-black uppercase tracking-widest">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Values */}
      <div className="mb-12">
        <p className="text-brand-orange font-black tracking-[0.4em] text-xs uppercase mb-3 text-center">What Drives Us</p>
        <h2 className="text-3xl md:text-4xl font-black text-brand-black text-center mb-10">OUR CORE VALUES</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {values.map((v, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 border border-black/6 shadow-sm hover:border-brand-orange/30 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-orange transition-colors duration-300">
                <v.icon size={22} className="text-brand-orange group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-black text-brand-black mb-2">{v.title}</h3>
              <p className="text-brand-muted text-sm leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="bg-brand-black rounded-3xl p-10 text-center"
      >
        <Truck size={40} className="text-brand-orange mx-auto mb-4" />
        <h3 className="text-3xl font-black text-white mb-3">Ready to Move With Us?</h3>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">Join thousands of businesses that trust PKRouteX for their logistics needs.</p>
        <a
          href="/#book"
          className="inline-block bg-brand-orange text-white px-10 py-4 rounded-full font-black tracking-widest text-sm hover:bg-brand-orange-light transition-all orange-glow"
        >
          BOOK A TRUCK NOW
        </a>
      </motion.div>
    </div>
  </div>
);

export default AboutUs;
